import { useCallback, useEffect, useMemo } from 'react'
import { atom, useAtom } from 'jotai'
import { useApiClient } from '../../../index'
import { isScopeIdRandomNanoId, scopeToString } from '../../scope'
import {
  defaultViewStateFromApi,
  getEmptyViewStateForScope,
  orderViewStatesByName,
  refreshedScopes,
  viewStatesFromApi,
  viewStateStore,
} from '../../store'
import {
  viewStateCache,
  createListCacheKey,
  createDefaultCacheKey,
} from '../../store/view-state-cache'
import { useViewState } from '../view-state'
import { useViewStateType } from '../view-state-type'
import type { IViewState, IViewType, ApiClientV1 } from '../../../index'
import type { Scope } from '../../types'

const fetchListViewStates = async ({
  apiClient,
  scope,
}: {
  apiClient: ApiClientV1['viewState']
  scope: Scope
}) => {
  const cacheKey = createListCacheKey(scope.type, scope.scope)

  return viewStateCache.execute(cacheKey, async () => {
    try {
      const res = await apiClient.listViewStates({
        typeName: scope.type,
        scope: scope.scope,
        limit: 200,
      })
      return res.viewStates
    } catch (error) {
      throw new Error('Failed to load list view states')
    }
  }) as Promise<IViewState[]>
}

const fetchDefaultViewState = async ({
  apiClient,
  type,
  scope,
}: {
  apiClient: ApiClientV1['viewState']
  type?: IViewType
  scope: Scope
}) => {
  if (!type) {
    throw new Error('Type is needed to get the default view state')
  }

  const cacheKey = createDefaultCacheKey(type.id, scope.scope)

  return viewStateCache.execute(cacheKey, async () => {
    try {
      return await apiClient.getDefaultViewState({
        typeId: type.id,
        scope: scope.scope,
      })
    } catch (error: any) {
      if (error && error.code === 'DefaultViewStateNotFound') {
        return getEmptyViewStateForScope(type, scope)
      }
      throw new Error('Failed to load default view states')
    }
  }) as Promise<IViewState>
}

/**
 * Provides base access to the global set of available view states from the
 * api.
 * Always gain access via this func as it ensures the given scope has the
 * required entries' setup.
 */
export const useViewStateApiAtom = (scope: Scope) => {
  const apiClient = useApiClient()?.viewState

  const type = useViewStateType(scope)

  const key = scopeToString(scope)

  const { setViewState: setViewStateInternal } = useViewState({
    scope,
  })

  const [getViewStatesList, setViewStatesList] = useAtom(viewStatesFromApi, {
    store: viewStateStore,
  })
  const [getDefaultViewStatesList, setDefaultViewStatesList] = useAtom(
    defaultViewStateFromApi,
    { store: viewStateStore },
  )

  // Initialise the current scope in the global atom.
  const scopeAtom = useMemo(() => {
    if (key in getViewStatesList) {
      return getViewStatesList[key]
    }

    // Init with null to distinguish "not yet loaded" from "loaded but empty".
    const apiViewStatesAtom = atom<IViewState[] | null>(null)
    apiViewStatesAtom.debugLabel = `viewstate.scope(${key}).api`

    // Add atoms to global atom.
    setViewStatesList((prev) => {
      return {
        ...prev,
        [key]: apiViewStatesAtom,
      }
    })
    return apiViewStatesAtom
  }, [key, getViewStatesList, setViewStatesList])

  const defaultViewStateScopeAtom = useMemo(() => {
    if (key in getDefaultViewStatesList) {
      return getDefaultViewStatesList[key]
    }

    // Init default atom with an empty view state.
    const defaultApiViewStatesAtom = atom<IViewState | undefined>(undefined)
    defaultApiViewStatesAtom.debugLabel = `viewstate.scope(${key}).defaultApi`

    // Add atoms to global atom.
    setDefaultViewStatesList((prev) => {
      return {
        ...prev,
        [key]: defaultApiViewStatesAtom,
      }
    })
    return defaultApiViewStatesAtom
  }, [key, getDefaultViewStatesList, setDefaultViewStatesList])

  const [viewStates, setViewStates] = useAtom(scopeAtom, {
    store: viewStateStore,
  })

  const [defaultViewState, setDefaultViewState] = useAtom(
    defaultViewStateScopeAtom,
    {
      store: viewStateStore,
    },
  )

  const refreshViewStates = useCallback(async () => {
    if (!apiClient) return

    const listViewStates = await fetchListViewStates({ apiClient, scope })
    const sortedListViewStates = listViewStates
      .filter((vs: IViewState) => !vs.isDefault)
      .sort(orderViewStatesByName)
    const defaultViewStateToLoad =
      defaultViewState ??
      (await fetchDefaultViewState({ apiClient, type, scope }))
    setDefaultViewState(defaultViewStateToLoad)
    const apiViewStatesList = [defaultViewStateToLoad, ...sortedListViewStates]
    setViewStates(apiViewStatesList)
    return apiViewStatesList
  }, [apiClient, type, scope, defaultViewState])

  // Always populate view states on mount.
  useEffect(() => {
    ;(async () => {
      if (!type?.id) return // Don't load default view state until a type exists
      if (refreshedScopes.includes(key)) return
      refreshedScopes.push(key)
      const apiViewStates = await refreshViewStates()

      if (apiViewStates && apiViewStates.length > 0) {
        // This is a workaround to stop the hardcoded unknown "Default View" displaying when there is no previously displayed saved view
        // and there is a default view from the API.
        // Ideally the default values should be fixed in "base-atom.ts" to return undefined, but we would still need this logic
        // to pre-select the API default view state once it's loaded.
        setViewStateInternal((prev) => {
          const validViewState = apiViewStates.find(
            (apiViewState) => apiViewState.id === prev.id,
          )
          return validViewState ? prev : apiViewStates[0]
        })
      }
    })()
    return () => {
      // if scopeId within key was generated by nanoid(), it will be different every time, so the code below removes the scope from the refreshedScopes array when the component unmounts to avoid the array growing indefinitely in these cases
      if (isScopeIdRandomNanoId(key)) {
        const index = refreshedScopes.indexOf(key)
        if (index !== -1) {
          refreshedScopes.splice(index, 1)
        }
      }
    }
  }, [type?.id])

  return {
    apiViewStates: viewStates,
    apiDefaultViewState: defaultViewState,
    setApiViewStates: setViewStates,
    setApiDefaultViewState: setDefaultViewState,
    refreshApiViewStates: refreshViewStates,
  }
}
